import type { MuiSxProps } from "@dealer-center/core-components";
import {
  Alert,
  Button,
  Modal,
  ModalContent,
  useToaster
} from "@dealer-center/core-components";
import { useEffect, useState } from "react";
import { useGetAccountId } from "@dealer-center/ddc-api-account-fetch";
import { BulkImportModalMain } from "./main";
import { BulkImportModalSpeedbump } from "./speedbump";
import {
  useInventoryConfigCreate,
  useInventoryConfigGetMany,
  useInventoryConfigUpdate,
  useInventoryConfigsGet
} from "@dealer-center/ddc-pages-inventory-config-fetch";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { checkIfConfigExists } from "./helper";

export const BulkImportModal = ({ sx }: { sx?: MuiSxProps }) => {
  const [shouldShowBuilkImportModal, setShouldShowBuilkImportModal] =
    useState(false);
  const { "cc9.website-inventory-config.show-bulk-import": shouldEnableSave } =
    useCC9Flags();
  const updateMutation = useInventoryConfigUpdate();
  const createMutation = useInventoryConfigCreate();

  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isImportDisabled, setIsImportDisabled] = useState(true);
  const [selectedConfigs, setSelectedConfigs] = useState<string[]>([]);
  const importedConfigDetailList = useInventoryConfigGetMany({
    accountId: selectedAccount,
    inventoryIds: selectedConfigs
  });
  const { toastSuccess, toastError } = useToaster();
  const currentAccountIdId = useGetAccountId();
  const { data: currentConfigList } =
    useInventoryConfigsGet(currentAccountIdId);
  const closeModal = () => {
    setShouldShowBuilkImportModal(false);
    setIsConfirmationVisible(false);
    setIsImportDisabled(true);
    setSelectedAccount(null);
    setSelectedConfigs([]);
  };

  const isAnyAccountLoading = importedConfigDetailList.some(
    config => config.isLoading
  );
  const isAnyAccountError = importedConfigDetailList.some(
    config => config.isError
  );
  // Disable the import button if:
  // 1. No account is selected
  // 2. The selected account is the same as the current account
  // 3. No configs are selected
  useEffect(() => {
    const isNoAccountSelected = selectedAccount === null;
    const isSameAccount = selectedAccount === currentAccountIdId;
    const isNoConfigsSelected = selectedConfigs.length === 0;
    setIsImportDisabled(
      isNoAccountSelected ||
        isSameAccount ||
        isNoConfigsSelected ||
        isAnyAccountLoading
    );
  }, [
    currentAccountIdId,
    selectedAccount,
    selectedConfigs,
    isAnyAccountLoading
  ]);

  const handleImportClick = () => {
    setIsConfirmationVisible(true);
  };

  const handleFinalImport = () => {
    if (!shouldEnableSave) {
      toastSuccess("Bulk Import is not enabled for this account", "");
      return;
    }

    if (!currentAccountIdId) {
      toastError("No account selected");
      return;
    }

    const mutations = importedConfigDetailList.map(config => {
      if (!config.data || config.isLoading || config.isError) {
        return Promise.reject(
          new Error("Error loading inventory config details")
        );
      }

      const configId = config.data.inventoryId;
      const dataToSave = {
        ...config.data,
        accountId: currentAccountIdId,
        id: `${currentAccountIdId}.${configId}`
      };

      const configExists = checkIfConfigExists(currentConfigList, configId);
      const mutation = configExists ? updateMutation : createMutation;
      const actionType = configExists ? "update" : "import";

      return mutation
        .mutateAsync(dataToSave)
        .then(() =>
          toastSuccess(`Config ${configId} ${actionType}ed successfully`, "")
        )
        .catch((error: Error) =>
          toastError(
            `Config ${configId} ${actionType} failed: ${error.message}`
          )
        );
    });

    Promise.all(mutations).then(closeModal);
  };

  useEffect(() => {
    if (selectedAccount === currentAccountIdId) {
      setIsImportDisabled(true);
    }
  }, [selectedAccount, currentAccountIdId]);
  return (
    <>
      <Button
        analyticsConfiguration={{
          eventName:
            "Website Inventory Config Bulk Import Configuration clicked"
        }}
        buttonStyle="secondary"
        sx={sx}
        onClick={() => setShouldShowBuilkImportModal(true)}
      >
        Bulk Import Configs
      </Button>
      <Modal
        footer={{
          primary: {
            label: `Import (${selectedConfigs.length})`,
            action: isConfirmationVisible
              ? handleFinalImport
              : handleImportClick,
            disabled:
              isImportDisabled ||
              createMutation.isPending ||
              updateMutation.isPending,
            isLoading:
              isAnyAccountLoading ||
              createMutation.isPending ||
              updateMutation.isPending
          },
          options: {
            label: isConfirmationVisible ? "Back" : "Cancel",
            action: () => {
              isConfirmationVisible
                ? setIsConfirmationVisible(false)
                : closeModal();
            }
          }
        }}
        header="Bulk Configuration"
        id="bulk-import-modal"
        show={shouldShowBuilkImportModal}
        size="large"
        onHide={() => {
          closeModal();
        }}
      >
        <ModalContent>
          {isAnyAccountError &&
            importedConfigDetailList
              .filter(config => config.isError)
              .map(config => (
                <Alert
                  key={config.dataUpdatedAt}
                  sx={{ mb: 2 }}
                  type="error"
                >{`${config.error.message}`}</Alert>
              ))}
          {isConfirmationVisible ? (
            <BulkImportModalSpeedbump
              currentAccountId={currentAccountIdId}
              selectedAccount={selectedAccount}
              selectedConfigs={selectedConfigs}
            />
          ) : (
            <BulkImportModalMain
              selectedAccount={selectedAccount}
              selectedConfigs={selectedConfigs}
              setSelectedAccount={setSelectedAccount}
              setSelectedConfigs={setSelectedConfigs}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
